import { useEffect, useState } from 'react';
import type { ActivityEvent, ActivityType } from '../socialApi';
import { pushPublicProfilePath } from '../shellHistory';
import { Badge } from '../ui/Badge';
import { Card } from '../ui/Card';
import { HopperIcon } from '../ui/icons/HopperIcon';
import { activitySubjectDestination } from './actionability';
import {
  EventResponseApiError,
  fetchEventResponseStatus,
  removeEventResponse,
  respondToEvent,
  type EventResponseChoice,
  type EventResponseStatus,
} from './eventResponseApi';
import { SocialProofLine } from './SocialProofLine';
import { PulsePhotoLightbox } from './PulsePhotoLightbox';

function relativeTime(value: string | null): string {
  if (!value) return 'только что';
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return 'недавно';
  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (seconds < 60) return 'только что';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} мин назад`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ч назад`;
  return `${Math.floor(hours / 24)} дн назад`;
}

function eventDateTime(value: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const datePart = new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date).replace(/\s*г\.$/u, '');
  const timePart = new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(date);

  return `${datePart} в ${timePart}`;
}

function verdict(value: unknown): { label: string; icon: 'impression-yes' | 'impression-maybe' | 'impression-no' } | null {
  if (value === 'yes') return { label: 'Да', icon: 'impression-yes' };
  if (value === 'maybe') return { label: 'Возможно', icon: 'impression-maybe' };
  if (value === 'no') return { label: 'Нет', icon: 'impression-no' };
  return null;
}

function assertNever(value: never): never {
  throw new Error(`Unhandled ActivityType: ${String(value)}`);
}

function eventLabel(type: ActivityType): string {
  switch (type) {
    case 'impression_created': return 'Впечатление';
    case 'follow_created': return 'Подписка';
    case 'new_place_discovered': return 'Новое место';
    case 'photo_uploaded': return 'Фото';
    case 'venue_offer_published': return 'Предложение';
    case 'venue_photo_published': return 'Фото заведения';
    case 'venue_event_published': return 'Событие';
    case 'venue_post_published': return 'Пост';
    case 'route_published': return 'Маршрут';
    case 'route_completed': return 'Маршрут пройден';
    case 'achievement_unlocked': return 'Достижение';
    case 'city_leader_changed': return 'Лидер города';
    case 'city_weekly_champion': return 'Чемпион недели';
    case 'venue_leader_changed': return 'Лидер места';
    case 'discovery_milestone': return 'Исследователь';
    case 'achievement_highlight': return 'Достижение города';
  }
  return assertNever(type);
}

function textPayload(event: ActivityEvent, key: string): string {
  const value = event.payload[key];
  return typeof value === 'string' ? value.trim() : '';
}

function positiveCount(value: unknown, fallback = 1): number {
  const numeric = Number(value ?? fallback);
  return Number.isFinite(numeric) && numeric > 0 ? Math.floor(numeric) : fallback;
}

function photoNoun(count: number): string {
  const value = Math.abs(count) % 100;
  const last = value % 10;
  if (value > 10 && value < 20) return 'фотографий';
  if (last === 1) return 'фотографию';
  if (last >= 2 && last <= 4) return 'фотографии';
  return 'фотографий';
}

function placeNoun(count: number): string {
  const value = Math.abs(count) % 100;
  const last = value % 10;
  if (value > 10 && value < 20) return 'мест';
  if (last === 1) return 'место';
  if (last >= 2 && last <= 4) return 'места';
  return 'мест';
}

type Presentation = {
  title: string;
  detail: string;
  timeDetail: string;
  mediaUrl: string | null;
  achievementIconUrl: string | null;
  placesCount: number | null;
};

function presentationFor(event: ActivityEvent, name: string, venueName: string | null, mediaCount: number): Presentation {
  let title = '';
  let detail = '';
  let timeDetail = '';
  let mediaUrl: string | null = null;
  let achievementIconUrl: string | null = null;
  let placesCount: number | null = null;

  switch (event.type) {
    case 'impression_created':
      title = venueName ? `Впечатление о ${venueName}` : `${name} поделился впечатлением`;
      detail = textPayload(event, 'text');
      break;
    case 'follow_created': {
      const followedName = textPayload(event, 'followed_user_display_name') || 'пользователя Хоппера';
      title = `${name} подписался на ${followedName}`;
      break;
    }
    case 'new_place_discovered':
      title = venueName || 'Новое место';
      detail = venueName ? `${name} первым открыл новое заведение на карте: «${venueName}»` : `${name} первым открыл новое место`;
      break;
    case 'photo_uploaded':
      title = mediaCount <= 1 ? `${name} добавил фотографию` : `${name} добавил ${mediaCount} ${photoNoun(mediaCount)}`;
      mediaUrl = textPayload(event, 'media_url') || textPayload(event, 'thumbnail_url') || null;
      break;
    case 'venue_offer_published':
      title = textPayload(event, 'offer_title') || `${name} опубликовало предложение`;
      detail = textPayload(event, 'offer_description');
      break;
    case 'venue_photo_published':
      title = mediaCount <= 1 ? `${name} добавило фотографию` : `${name} добавило ${mediaCount} ${photoNoun(mediaCount)}`;
      mediaUrl = textPayload(event, 'media_url') || textPayload(event, 'thumbnail_url') || null;
      break;
    case 'venue_event_published':
      title = textPayload(event, 'title') || `${name} опубликовало событие`;
      detail = textPayload(event, 'description');
      timeDetail = eventDateTime(textPayload(event, 'starts_at'));
      mediaUrl = textPayload(event, 'cover_url') || null;
      break;
    case 'venue_post_published':
      title = textPayload(event, 'title') || `${name} опубликовало пост`;
      detail = textPayload(event, 'body');
      mediaUrl = textPayload(event, 'cover_url') || null;
      break;
    case 'route_published':
      title = textPayload(event, 'title') || `${name} опубликовал маршрут`;
      mediaUrl = textPayload(event, 'cover_url') || null;
      placesCount = positiveCount(event.payload.places_count, 0);
      break;
    case 'route_completed':
      title = textPayload(event, 'title') || `${name} прошёл маршрут`;
      placesCount = positiveCount(event.payload.places_count, 0);
      break;
    case 'achievement_unlocked': {
      const achievement = textPayload(event, 'achievement_name');
      title = achievement ? `${name} получил достижение «${achievement}»` : `${name} получил достижение`;
      achievementIconUrl = textPayload(event, 'icon_url') || null;
      break;
    }
    case 'city_leader_changed':
      title = `${name} вышел на 1-е место в городе`;
      detail = event.city?.name ? `Новый лидер города ${event.city.name}` : 'Новый лидер городского рейтинга';
      break;
    case 'city_weekly_champion':
      title = `${name} стал чемпионом недели`;
      detail = event.city?.name ? `Лучший результат недели · ${event.city.name}` : 'Лучший результат недели';
      break;
    case 'venue_leader_changed':
      title = venueName ? `${name} вышел на 1-е место в «${venueName}»` : `${name} стал лидером заведения`;
      detail = 'Лидер по локальному XP';
      break;
    case 'discovery_milestone': {
      const threshold = Number(event.payload.threshold ?? 0);
      title = threshold > 0 ? `${name} открыл ${threshold} ${placeNoun(threshold)}` : `${name} достиг новой отметки исследователя`;
      detail = textPayload(event, 'milestone') || 'Новая отметка исследователя';
      break;
    }
    case 'achievement_highlight': {
      const achievement = textPayload(event, 'achievement_name');
      const firstInCity = event.payload.highlight_mode === 'first_in_city';
      title = achievement
        ? `${name} ${firstInCity ? 'первым в городе получил' : 'получил'} «${achievement}»`
        : `${name} ${firstInCity ? 'первым в городе получил достижение' : 'получил заметное достижение'}`;
      detail = firstInCity ? 'Первое такое достижение в городе' : 'Заметное достижение в Хоппере';
      break;
    }
    default:
      return assertNever(event.type);
  }

  return { title, detail, timeDetail, mediaUrl, achievementIconUrl, placesCount };
}

function responseErrorText(reason: unknown): string {
  if (reason instanceof EventResponseApiError) {
    if (reason.status === 401) return 'Войдите, чтобы отметить событие';
    if (reason.status === 422) return reason.message || 'Сейчас изменить отметку нельзя';
  }
  return 'Не удалось изменить отметку';
}

type FeedCardProps = {
  event: ActivityEvent;
  currentUserId: string | null;
  onOpenUser: (userId: string) => void;
  onOpenVenue: (venueId: string) => void;
  onShowVenueOnMap?: (venueId: string) => void;
  contextCityName?: string | null;
  presentation?: 'compact' | 'pulse';
};

export function FeedCard({ event, currentUserId, onOpenUser, onOpenVenue, onShowVenueOnMap, contextCityName = null, presentation = 'compact' }: FeedCardProps) {
  const isUserAuthor = event.author.type !== 'venue';
  const name = isUserAuthor
    ? event.author.display_name?.trim() || 'Гость Хоппера'
    : event.author.name?.trim() || event.venue?.name?.trim() || 'Заведение Хоппера';
  const venueName = event.venue?.name ?? (typeof event.payload.venue_name === 'string' ? event.payload.venue_name : null);
  const venueId = event.venue?.id ?? (typeof event.payload.venue_id === 'string' ? event.payload.venue_id : null);
  const mediaCount = positiveCount(event.payload.media_count, 1);
  const content = presentationFor(event, name, venueName, mediaCount);
  const title = content.title;
  const impression = event.type === 'impression_created' ? verdict(event.payload.verdict) : null;
  const defaultDestination = activitySubjectDestination(event, currentUserId);
  const subjectDestination = presentation === 'pulse' && event.type === 'photo_uploaded'
    ? (event.author.id ? `/profile/${encodeURIComponent(event.author.id)}` : null)
    : presentation === 'pulse' && event.type === 'venue_photo_published'
      ? (venueId ? `/venue/${encodeURIComponent(venueId)}` : null)
      : defaultDestination;
  const isPhotoCard = presentation === 'pulse' && (event.type === 'photo_uploaded' || event.type === 'venue_photo_published');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const eventId = event.type === 'venue_event_published' ? textPayload(event, 'event_id') : '';
  const isEventPulse = presentation === 'pulse' && event.type === 'venue_event_published';
  const [eventResponse, setEventResponse] = useState<EventResponseStatus | null>(null);
  const [eventResponseLoading, setEventResponseLoading] = useState(false);
  const [eventResponseError, setEventResponseError] = useState<string | null>(null);

  useEffect(() => {
    setEventResponse(null);
    setEventResponseError(null);

    if (!isEventPulse || !eventId || !currentUserId) return undefined;

    const controller = new AbortController();
    void fetchEventResponseStatus(eventId, controller.signal)
      .then((status) => setEventResponse(status))
      .catch((reason: unknown) => {
        if (!(reason instanceof DOMException && reason.name === 'AbortError')) {
          setEventResponseError(responseErrorText(reason));
        }
      });

    return () => controller.abort();
  }, [currentUserId, eventId, isEventPulse]);

  const openUser = (userId: string) => {
    pushPublicProfilePath(userId);
    onOpenUser(userId);
  };

  const titleNode = subjectDestination && !isEventPulse ? (
    <button className="pulse-feed-card__title pulse-link-button" type="button" onClick={() => window.location.assign(subjectDestination)}>{title}</button>
  ) : (
    <h2 className="pulse-feed-card__title">{title}</h2>
  );

  if (presentation === 'compact') {
    return (
      <Card className={`pulse-feed-card pulse-feed-card--${event.type}`}>
        <div className="pulse-feed-card__head">
          <Badge tone="neutral">{eventLabel(event.type)}</Badge>
          <SocialProofLine
            people={isUserAuthor && event.author.id ? [{ id: event.author.id, name }] : []}
            text={name}
            timestamp={relativeTime(event.created_at)}
            onOpenPerson={isUserAuthor ? openUser : undefined}
          />
        </div>
        {titleNode}
        {content.detail && <p className="pulse-feed-card__text">{content.detail}</p>}
        <div className="pulse-feed-card__footer">
          {impression && <span className="pulse-impression-verdict"><HopperIcon name={impression.icon} size={18} /><span>{impression.label}</span></span>}
          {venueId && venueName && <button className="pulse-feed-card__venue pulse-link-button" type="button" onClick={() => onOpenVenue(venueId)}><HopperIcon name="pin" size={16} /><span>{venueName}</span></button>}
        </div>
      </Card>
    );
  }

  const authorInitial = name.slice(0, 1).toUpperCase();
  // Only the city-scoped "Все" feed may fall back to its selected city.
  const authorCityName = event.city?.name?.trim() || contextCityName?.trim() || null;
  const authorVenueId = !isUserAuthor ? (event.author.id || venueId) : null;
  const canOpenAuthor = Boolean(isUserAuthor ? event.author.id : authorVenueId);
  const openAuthor = () => {
    if (isUserAuthor && event.author.id) openUser(event.author.id);
    else if (authorVenueId) onOpenVenue(authorVenueId);
  };
  const showVenueLink = Boolean(venueId && venueName)
    && event.type !== 'follow_created'
    && event.type !== 'venue_photo_published'
    && event.type !== 'route_published'
    && event.type !== 'route_completed';
  const cardClickable = Boolean(subjectDestination);
  const eventMediaClass = isEventPulse && content.mediaUrl ? ' has-event-media' : '';

  const handleEventResponse = async (choice: EventResponseChoice) => {
    if (!eventId || !currentUserId || eventResponseLoading) return;
    setEventResponseLoading(true);
    setEventResponseError(null);
    try {
      const status = eventResponse?.my_response === choice
        ? await removeEventResponse(eventId)
        : await respondToEvent(eventId, choice);
      setEventResponse(status);
    } catch (reason: unknown) {
      setEventResponseError(responseErrorText(reason));
    } finally {
      setEventResponseLoading(false);
    }
  };

  return (
    <>
    <Card
      className={`pulse-feed-card is-rich pulse-feed-card--${event.type}${eventMediaClass}${cardClickable ? ' is-subject-clickable' : ''}`}
      variant={cardClickable ? 'interactive' : 'default'}
      role={cardClickable ? 'link' : undefined}
      tabIndex={cardClickable ? 0 : undefined}
      onClick={cardClickable ? (click) => {
        if ((click.target as HTMLElement).closest('button, a')) return;
        window.location.assign(subjectDestination as string);
      } : undefined}
      onKeyDown={cardClickable ? (keyboardEvent) => {
        if (keyboardEvent.target !== keyboardEvent.currentTarget) return;
        if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
          keyboardEvent.preventDefault();
          window.location.assign(subjectDestination as string);
        }
      } : undefined}
    >
      <div className="pulse-feed-card__rich-body">
        <div className="pulse-feed-card__rich-head">
          <div className="pulse-feed-card__author">
            {canOpenAuthor ? (
              <button type="button" className="pulse-feed-card__author-avatar-action" aria-label={`Открыть ${isUserAuthor ? 'профиль' : 'заведение'}: ${name}`} onClick={(click) => { click.stopPropagation(); openAuthor(); }}>
                {event.author.avatar_url ? <img className="pulse-feed-card__author-avatar" src={event.author.avatar_url} alt="" /> : <span className="pulse-feed-card__author-avatar is-empty" aria-hidden="true">{authorInitial}</span>}
              </button>
            ) : event.author.avatar_url ? <img className="pulse-feed-card__author-avatar" src={event.author.avatar_url} alt="" /> : <span className="pulse-feed-card__author-avatar is-empty" aria-hidden="true">{authorInitial}</span>}
            <span className="pulse-feed-card__author-copy">
              {canOpenAuthor ? <button type="button" className="pulse-feed-card__author-name" onClick={(click) => { click.stopPropagation(); openAuthor(); }}>{name}</button> : <span className="pulse-feed-card__author-name">{name}</span>}
              <span className="pulse-feed-card__meta">{relativeTime(event.created_at)}{authorCityName ? ` · ${authorCityName}` : ''}</span>
            </span>
          </div>
          {isEventPulse ? <h2 className="pulse-feed-card__event-title">{title}</h2> : <span className="pulse-feed-card__kind">{eventLabel(event.type)}</span>}
          {event.type === 'new_place_discovered' && <span className="pulse-feed-card__mobile-new-place-title">{title}</span>}
        </div>

        {isEventPulse ? (
          <>
            {!content.mediaUrl && <span className="pulse-feed-card__kind pulse-feed-card__event-kind-inline">{eventLabel(event.type)}</span>}
            {content.detail && <p className="pulse-feed-card__event-description" title={content.detail}>{content.detail}</p>}
            {content.timeDetail && <p className="pulse-feed-card__event-time">{content.timeDetail}</p>}
          </>
        ) : (
          <>
            {event.type === 'achievement_unlocked' && content.achievementIconUrl ? (
              <div className="pulse-feed-card__achievement-line"><img className="pulse-feed-card__achievement-icon" src={content.achievementIconUrl} alt="" />{titleNode}</div>
            ) : <div className={event.type === 'new_place_discovered' ? 'pulse-feed-card__new-place-title' : undefined}>{titleNode}</div>}
            {content.detail && <p className="pulse-feed-card__text">{content.detail}</p>}
          </>
        )}
        {content.placesCount !== null && content.placesCount > 0 && <div className="pulse-feed-card__route-meta">{content.placesCount} {placeNoun(content.placesCount)} в маршруте</div>}
        {content.achievementIconUrl && event.type !== 'achievement_unlocked' && <img className="pulse-feed-card__achievement-icon" src={content.achievementIconUrl} alt="" />}
      </div>

      {isEventPulse && content.mediaUrl ? (
        <div className="pulse-feed-card__event-media">
          <span className="pulse-feed-card__kind">{eventLabel(event.type)}</span>
          <img className="pulse-feed-card__media" src={content.mediaUrl} alt="" loading="lazy" />
        </div>
      ) : content.mediaUrl ? isPhotoCard ? <button type="button" className="pulse-feed-card__photo-button" aria-label="Открыть фотографию" onClick={(click) => { click.stopPropagation(); setLightboxOpen(true); }}><img className="pulse-feed-card__media" src={content.mediaUrl} alt="" loading="lazy" /></button> : <img className="pulse-feed-card__media" src={content.mediaUrl} alt="" loading="lazy" /> : null}

      {(impression || showVenueLink || (isEventPulse && eventId)) && (
        <div className="pulse-feed-card__footer">
          {impression && <span className="pulse-impression-verdict"><HopperIcon name={impression.icon} size={18} /><span>{impression.label}</span></span>}
          {showVenueLink && venueId && venueName && <button className="pulse-feed-card__venue pulse-link-button" type="button" onClick={(click) => { click.stopPropagation(); if (event.type === 'new_place_discovered') onShowVenueOnMap?.(venueId); else onOpenVenue(venueId); }}><HopperIcon name="pin" size={16} /><span>{venueName}</span>{event.type === 'new_place_discovered' && event.venue?.address?.trim() && <small className="pulse-feed-card__venue-address" title={event.venue.address}>{event.venue.address}</small>}</button>}
          {isEventPulse && eventId && (
            <div className="pulse-event-response-area" onClick={(click) => click.stopPropagation()}>
              <div className="pulse-event-response-actions" aria-label="Ваш ответ на событие">
                <button
                  type="button"
                  className={eventResponse?.my_response === 'going' ? 'is-active' : ''}
                  aria-pressed={eventResponse?.my_response === 'going'}
                  disabled={!currentUserId || eventResponseLoading || Boolean(eventResponse?.is_full && eventResponse.my_response !== 'going')}
                  title={!currentUserId ? 'Войдите, чтобы отметить событие' : undefined}
                  onClick={() => void handleEventResponse('going')}
                >
                  Пойду
                </button>
                <button
                  type="button"
                  className={eventResponse?.my_response === 'interested' ? 'is-active' : ''}
                  aria-pressed={eventResponse?.my_response === 'interested'}
                  disabled={!currentUserId || eventResponseLoading}
                  title={!currentUserId ? 'Войдите, чтобы отметить событие' : undefined}
                  onClick={() => void handleEventResponse('interested')}
                >
                  Интересно
                </button>
              </div>
              {eventResponseError && <span className="pulse-event-response-error" role="status">{eventResponseError}</span>}
            </div>
          )}
        </div>
      )}
    </Card>
    {isPhotoCard && lightboxOpen && content.mediaUrl && <PulsePhotoLightbox src={content.mediaUrl} onClose={() => setLightboxOpen(false)} />}
    </>
  );
}
